import { WorkItemResult } from "@buzzeasy/shared-frontend-utilities";
import { Spin, Typography } from "antd";
import { List } from "antd-mobile";
import { ReactElement, useMemo, useState } from "react";
import { styled } from "styled-components";
import { useDebouncedCallback } from "use-debounce";
import { routes } from "../../App";
import SideMenu from "../../components/containers/SideMenu";
import LayoutWithNav from "../../components/layouts/LayoutWithNav";
import ErrorWithRetry from "../../components/presenters/ErrorWithRetry";
import TaskHistoryFilters from "../../components/presenters/TaskHistoryFilters";
import TaskListItem from "../../components/presenters/TaskListItem";
import { useSearchWorkItemsQuery } from "../../redux/apis/historicalDataApi";

const { Title, Text } = Typography;

const POLLING_INTERVAL = 60_000;

export default function TaskHistoryPage(): ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedResult, setSelectedResult] = useState<string>("");
  const [openPopupItemId, setOpenPopupItemId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useSearchWorkItemsQuery(
    {
      searchTerm: searchText,
      result: selectedResult ? selectedResult as WorkItemResult : undefined,
    },
    {
      pollingInterval: POLLING_INTERVAL,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
    });

  const debouncedSetSearchText = useDebouncedCallback((value: string) => {
    setSearchText(value);
  }, 500);

  const listContent = useMemo(
    () => {
      if (isFetching)
        return centeredSpin;

      if (data?.workItems?.length === 0) {
        return (
          <Centered>
            <Text style={{ fontStyle: "italic" }}>No tasks found</Text>
          </Centered>
        );
      }

      return (
        data?.workItems?.map((wi) => {
          const primaryConversation = wi.conversations?.find(c => c.id === wi.primaryConversationId);
          return (
            <TaskListItem
              key={wi.id}
              workItem={wi}
              isOutbound={primaryConversation?.isOutbound ?? true}
              phoneNumber={primaryConversation?.customerPhoneNumber ?? "Unknown number"}
              isFilteredView={!!selectedResult}
              isPopupOpen={openPopupItemId === wi.id}
              onPopupOpenChange={isOpen => setOpenPopupItemId(isOpen ? wi.id : null)}
            />
          );
        })
      );
    },
    [data?.workItems, isFetching, selectedResult, openPopupItemId],
  );

  const mainContent = useMemo(
    () => {
      if (isError)
        return <ErrorWithRetry title="Failed to load tasks" onRetry={refetch} />;

      if (isLoading)
        return centeredSpin;

      return (
        <>
          <TaskHistoryFilters
            onSearch={setSearchText}
            onSearchTextChange={debouncedSetSearchText}
            onSelectedResultChange={setSelectedResult}
            selectedResult={selectedResult}
          />
          <StyledList>
            {listContent}
          </StyledList>
        </>
      );
    },
    [debouncedSetSearchText, isError, isLoading, listContent, refetch, selectedResult],
  );

  return (
    <LayoutWithNav
      mainContent={
        <Container>
          <Title level={3}>Task History</Title>
          {mainContent}
        </Container>
      }
      menuContent={
        <SideMenu
          activeTabKey={routes.taskHistory}
          onCloseMenu={() => setIsMenuOpen(false)}
        />
      }
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    />
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: ${p => p.theme.padding}px;
  padding: ${p => p.theme.paddingLG}px ${p => p.theme.padding}px 0;
  background-color: ${p => p.theme.colorBgContainer};
`;

const StyledList = styled(List)`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: ${p => p.theme.padding}px;
  
  --border-bottom: none;
  --border-top: none;
  --border-inner: none;
`;

const Centered = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const centeredSpin = (
  <Centered style={{ paddingTop: "1rem" }}>
    <Spin />
  </Centered>
);
